import { createFormHook, formOptions } from "@tanstack/react-form";
import { fieldContext, formContext } from "./Form";
import { Input, TextArea, DateInput, TimeInput, UserSelect } from "./FormComponents";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import type { TaskWithOccurrence } from "@dashboard/utils/taskWithOccurrence";
import { useCreateTask, useUpdateTask } from "@dashboard/hooks/useTaskQuery";
import type { CreateTaskInput, UpdateTaskInput } from "@dashboard/api/task";
import { RRule } from "rrule";
import { useHseInstance } from "@dashboard/hooks/useHseInstance";

type TaskFormValues = {
    title: string;
    description?: string;
    responsible: number;
    endDate: string;
    time?: string;
};

export const VALIDATION_LIMITS = {
    TITLE_MAX_LENGTH: 100,
    DESCRIPTION_MAX_LENGTH: 500,
} as const;

const { useAppForm: useTaskForm, withForm } = createFormHook({
    fieldContext,
    formContext,
    fieldComponents: { Input, TextArea, DateInput, TimeInput, UserSelect },
    formComponents: {},
});

export const useTaskFormSetup = () => {
    const { t } = useTranslation();
    const { instanceId } = useHseInstance();
    const createMutation = useCreateTask();
    const updateMutation = useUpdateTask();

    const mapFormToCreateTask = (formValues: TaskFormValues): CreateTaskInput => {
        const dateAtMidnightUTC = new Date(formValues.endDate + "T00:00:00Z");
        const rule = new RRule({
            freq: RRule.DAILY,
            dtstart: dateAtMidnightUTC,
            count: 1,
        });
        return {
            title: formValues.title,
            description: formValues.description || null,
            instanceId,
            annualCycle: true,
            status: "ACTIVE",
            ownerUserId: formValues.responsible || undefined,
            rrule: rule.toString(),
        };
    };

    const mapFormToUpdateTask = (
        formValues: TaskFormValues,
        isRecurring: boolean,
    ): UpdateTaskInput => {
        const updateData: UpdateTaskInput = {
            title: formValues.title,
            description: formValues.description || null,
            ownerUserId: formValues.responsible,
        };

        if (!isRecurring) {
            const dateAtMidnightUTC = new Date(formValues.endDate + "T00:00:00Z");
            const rule = new RRule({
                freq: RRule.DAILY,
                dtstart: dateAtMidnightUTC,
                count: 1,
            });
            updateData.rrule = rule.toString();
        }

        return updateData;
    };

    const handleTaskSubmit = async (
        formValues: TaskFormValues,
        taskId?: number,
        isRecurring = false,
    ) => {
        try {
            if (taskId) {
                // Update existing task
                const updateData = mapFormToUpdateTask(formValues, isRecurring);
                await updateMutation.mutateAsync({ id: taskId, data: updateData });
                return { success: true as const };
            } else {
                // Create new task
                const createData = mapFormToCreateTask(formValues);
                const task = await createMutation.mutateAsync(createData);
                return { success: true as const, taskId: task.id };
            }
        } catch (error) {
            console.error("Failed to save task:", error);
            return { success: false as const, error };
        }
    };

    const mapTaskToForm = (task: TaskWithOccurrence): TaskFormValues => ({
        title: task.title || "",
        description: task.description || "",
        responsible: task.ownerUserId || 0,
        endDate: task.dueDate.toISOString().split("T")[0],
        time: "",
    });

    const formSchema = z.object({
        title: z
            .string()
            .min(1, t("common.titleIsRequired"))
            .max(VALIDATION_LIMITS.TITLE_MAX_LENGTH, t("common.titleMaxLength")),

        description: z
            .string()
            .max(VALIDATION_LIMITS.DESCRIPTION_MAX_LENGTH, t("common.descriptionMaxLength"))
            .optional(),

        responsible: z.number().min(1, t("common.responsiblePersonIsRequired")),
        endDate: z
            .string()
            .min(1, t("common.endDateIsRequired"))
            .refine((value) => {
                const parsed = new Date(value + "T00:00:00Z");
                if (Number.isNaN(parsed.getTime())) return false;
                const year = parsed.getUTCFullYear();
                return year >= 1900 && year <= 9999;
            }, t("common.invalidEndDate")),
        time: z.string().optional(),
    });

    const formOpts = formOptions({
        defaultValues: {
            title: "",
            description: "",
            responsible: 0,
            endDate: "",
            time: "",
        } as TaskFormValues,
        validators: {
            onSubmit: formSchema,
        },
    });
    return {
        useTaskForm,
        withForm,
        formOpts,
        mapTaskToForm,
        handleTaskSubmit,
        mapFormToCreateTask,
        mapFormToUpdateTask,
        submitError: createMutation.error || updateMutation.error,
    };
};
